import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, X, Send, Phone, User, Bot, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [humanRequested, setHumanRequested] = useState(false);
  const messagesEndRef = useRef(null);
  const { isAuthenticated } = useAuth();
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Start chat session
  const startSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([{
        message_id: 'greeting',
        sender_type: 'ai',
        sender_name: 'MedConnect AI',
        message: data.greeting,
        created_at: new Date().toISOString()
      }]);
    } catch (err) {
      console.error('Failed to start chat:', err);
    }
  };

  // Open chat
  const handleOpen = () => {
    setIsOpen(true);
    if (!sessionId) startSession();
  };

  // Poll for new messages when human is requested or admin joined
  useEffect(() => {
    if (!sessionId || !humanRequested) return;
    
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}/messages`, {
          credentials: 'include'
        });
        if (res.ok) {
          const msgs = await res.json();
          setMessages(msgs);
        }
      } catch {}
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [sessionId, humanRequested]);

  // Send message
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMsg = input.trim();
    setInput('');
    setLoading(true);

    // Optimistic add
    setMessages(prev => [...prev, {
      message_id: `temp_${Date.now()}`,
      sender_type: 'user',
      sender_name: 'You',
      message: userMsg,
      created_at: new Date().toISOString()
    }]);

    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMsg })
      });
      const data = await res.json();
      
      if (data.ai_response) {
        setMessages(prev => [...prev, data.ai_response]);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setLoading(false);
    }
  };

  // Request human
  const requestHuman = async () => {
    try {
      await fetch(`${API_URL}/api/chat/sessions/${sessionId}/request-human`, {
        method: 'POST',
        credentials: 'include'
      });
      setHumanRequested(true);
      setMessages(prev => [...prev, {
        message_id: `sys_${Date.now()}`,
        sender_type: 'system',
        sender_name: 'System',
        message: "You've been connected to our support queue. An operator will join shortly. You can also reach us directly on WhatsApp at +355 68 608 9622.",
        created_at: new Date().toISOString()
      }]);
    } catch (err) {
      console.error('Failed to request human:', err);
    }
  };

  const getMessageStyle = (type) => {
    switch (type) {
      case 'user': return 'bg-[#1a3c5e] text-white ml-auto';
      case 'ai': return 'bg-slate-100 text-slate-800';
      case 'admin': return 'bg-teal-50 text-teal-900 border border-teal-200';
      case 'system': return 'bg-amber-50 text-amber-800 text-center text-xs italic mx-auto';
      default: return 'bg-slate-100';
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'user': return <User className="w-3.5 h-3.5" />;
      case 'ai': return <Bot className="w-3.5 h-3.5" />;
      case 'admin': return <User className="w-3.5 h-3.5" />;
      default: return null;
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          data-testid="chat-widget-button"
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-[9999] w-14 h-14 bg-[#1a3c5e] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#15334f] transition-all hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chat-widget-window"
          className="fixed bottom-4 right-4 z-[9999] w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
        >
          {/* Header */}
          <div className="bg-[#1a3c5e] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <p className="font-semibold text-sm">MED CONNECT</p>
                <p className="text-[10px] text-white/70">AI Assistant</p>
              </div>
            </div>
            <button
              data-testid="chat-widget-close"
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50/50">
            {messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${getMessageStyle(msg.sender_type)}`}
              >
                {msg.sender_type !== 'system' && msg.sender_type !== 'user' && (
                  <div className="flex items-center gap-1 mb-0.5 text-[10px] font-medium opacity-70">
                    {getIcon(msg.sender_type)}
                    {msg.sender_name}
                  </div>
                )}
                <p className="whitespace-pre-wrap">{msg.message}</p>
              </div>
            ))}
            {loading && (
              <div className="max-w-[85%] px-3 py-2 rounded-xl bg-slate-100 text-slate-500 text-sm">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {!humanRequested && messages.length > 1 && (
            <div className="px-3 py-1.5 border-t border-slate-100 flex gap-1.5 shrink-0 bg-white">
              <button
                data-testid="chat-request-human"
                onClick={requestHuman}
                className="text-[11px] px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 flex items-center gap-1 transition"
              >
                <User className="w-3 h-3" />Talk to human
              </button>
              <a
                href="https://wa.me/355686089622"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-whatsapp-link"
                className="text-[11px] px-2.5 py-1 bg-green-50 hover:bg-green-100 rounded-full text-green-700 flex items-center gap-1 transition"
              >
                <Phone className="w-3 h-3" />WhatsApp
              </a>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-slate-100 bg-white shrink-0">
            <div className="flex items-center gap-2">
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                className="flex-1 text-sm px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3c5e]/20 focus:border-[#1a3c5e]"
                disabled={loading}
              />
              <button
                data-testid="chat-send-button"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-9 h-9 bg-[#1a3c5e] text-white rounded-xl flex items-center justify-center hover:bg-[#15334f] transition disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
